import {
  type OfferRedPointsV1,
  type OfferV11,
} from '@redteclab/api/clients/bully'

/**
 * returns true if the current time is less than end data and more than start data
 * for cases:
 * - no start date - true if lt enddate
 * - no end date - true if gt startdate
 * - both start and end date - true if gt startdate and lt enddate
 */
export const productAccessorGetCurrentTimeIsInRedpointsTimeframe = (
  redPoints: OfferRedPointsV1,
): boolean => {
  const endDate = new Date(redPoints.endDate ?? 0).getTime()
  const startDate = new Date(redPoints.startDate ?? 0).getTime()
  const now = Date.now()

  const noEndDateAndNowIsAfterStart = endDate === 0 && now > startDate

  if (noEndDateAndNowIsAfterStart) {
    return true
  }

  const noStartDateAndNowIsBeforeEnd = startDate === 0 && endDate > now

  if (noStartDateAndNowIsBeforeEnd) {
    return true
  }

  return now >= startDate && now <= endDate
}

/**
 * shortcut to get the redpoints object from offer
 */
export const productAccessorGetRedPointsForOffer = (
  offer: OfferV11,
): OfferRedPointsV1 | undefined => {
  return offer.offerPrice?.additionalInformation?.redPoints ?? undefined
}

/**
 * returns redpoints multiplier
 * defaults to 0 if less than 1 (in legacy code it could be e.g. -1)
 */
export const productAccessorGetRedPointsMultiplier = (
  redPoints: OfferRedPointsV1,
): number => {
  const multiplier = redPoints.multiplier ?? 0
  if (multiplier < 1) {
    return 0
  }

  return multiplier
}

const productAccessorGetRedPointsAmountWithoutMultiplier = (
  redPoints: OfferRedPointsV1,
): number => {
  return redPoints.amount ?? 0
}

export const productAccessorGetRedPointsRedPointsAmountWithMultiplier = (
  offer: OfferV11,
): number => {
  // the multiplier is applied in the backend, just checking the rest of conditions
  const redPoints = productAccessorGetRedPointsForOffer(offer)

  if (!redPoints) {
    return 0
  }

  const amount = productAccessorGetRedPointsAmountWithoutMultiplier(redPoints)
  const multiplier = productAccessorGetRedPointsMultiplier(redPoints)

  if (multiplier < 1) {
    return 0
  }

  if (!productAccessorGetCurrentTimeIsInRedpointsTimeframe(redPoints)) {
    return amount / multiplier
  }

  return amount
}
